import React from 'react';
import moment from 'moment';
import {StyleSheet, View} from 'react-native';
import Localized from 'src/constants/AppStrings';
import Styles from '../../../../Styles';
import AVFormattedCurrency from '../../../AVFormattedCurrency';
import type {HistoryItem as HistoryItemType} from 'src/types/TransactionDetail';
import AVTouchableOpacity from '../../../AVTouchableOpacity';
import AccountStore from 'src/stores/AccountStore';
import AccountConstants from 'src/constants/AccountConstants';
import AVText from '../../../AVText';
import RightIcon from 'src/components/img/svg/RightIcon';
import Settings from 'src/Settings';
import {getDescriber} from 'src/components/elements/descriptor/DescriptorType';
import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import {buildTypeArray} from 'src/Util';
import {MessageTypeEnum} from 'src/constants/MessageTypeEnum';
import {useGlobalize} from '../../../../../i18n/hook/useGlobalize';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

type NewHistoryItemProps = {
  historyItem: HistoryItemType;
  length?: number;
  index: number;
  onPress: (item: HistoryItemType) => void;
};

export const Reason = {
  SignUp: 'Referral-Signup',
  Referral: MessageTypeEnum.referral,
};

export const NewHistoryItem = (props: NewHistoryItemProps) => {
  let {Amount} = props.historyItem;
  const {
    TransactionTypeDisplay = Localized.Labels.purchase,
    Type,
    Points,
    currency,
    BalanceTypeDisplay = Localized.Labels.my_account,
    TransDate,
  } = props.historyItem;
  const {formatCurrency} = useGlobalize();

  const formattedCurrency = formatCurrency(Amount, AccountStore.getCurrency(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })?.replace(AccountStore.getCurrency().substring(0, 2), '');

  if (Type === AccountConstants.SALE_TYPE) {
    Amount *= -1;
  }

  const date = moment(TransDate).format('MMM D');

  const typeArr = buildTypeArray(Type, TransactionTypeDisplay);

  const isItemClickable = typeArr.includes(Type);

  const points = Number(Points);

  const descriptionTextColor = Settings.isRefiveAnd365Pay()
    ? Styles.black
    : Styles.darkColor;
  const getTransactionTypeDisplay = () => {
    if (TransactionTypeDisplay === 'Snack Credit') {
      return Localized.Labels.send_a_snack_title;
    } else if (['Sign-Up Reward'].indexOf(TransactionTypeDisplay) !== -1) {
      return Localized.Labels.received_reward;
    } else if (['Referral Reward'].indexOf(TransactionTypeDisplay) !== -1) {
      return Localized.Labels.received_reward;
    } else {
      return TransactionTypeDisplay;
    }
  };
  return (
    <AVTouchableOpacity
      key={`${props.index} ${props.historyItem.Id}`}
      accessible={true}
      style={styles.container}
      disabled={!isItemClickable}
      onPress={() => props.onPress(props.historyItem)}
      testID="qa_btn_ReceiptItem"
    >
      <View style={styles.mainContainer}>
        <View style={styles.leftViewContainer}>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            numberOfLines={1}
            style={[
              getDescriber().historyListStyleDescriptor().historyHeaderTitleDes,
              getSnackDescriber().snackDetailsBtnFontSTyle(),
            ]}
            accessible={true}
            accessibilityLabel={`${getTransactionTypeDisplay()}, `}
            aria-label={`${getTransactionTypeDisplay()}, `}
            testID="qa_ReceiptType_txt"
            nativeID="qa_ReceiptType_txt"
          >
            {getTransactionTypeDisplay()}
          </AVText>

          <AVText
            accessible={true}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            style={[
              getDescriber().historyListStyleDescriptor().balanceType,
              getSnackDescriber().snackDetailsBtnFontSTyle(),
            ]}
            aria-label={`${BalanceTypeDisplay}, `}
            accessibilityLabel={`${BalanceTypeDisplay}, `}
            testID="qa_FundingType_txt"
            nativeID="qa_FundingType_txt"
          >
            {BalanceTypeDisplay}
          </AVText>

          {!isNaN(points) && points > 0 && (
            <AVText
              style={[
                getDescriber().historyListStyleDescriptor().pointsStyle,
                getDescriber().historyListStyleDescriptor()
                  .historyTabFontFamily,
                {
                  color:
                    Type === AccountConstants.FUNDING_TYPE
                      ? getDescriber().fundingAmountColor()
                      : descriptionTextColor,
                },
              ]}
              accessible={true}
              testID="qa_EarnedPoints_txt"
              nativeID="qa_EarnedPoints_txt"
              aria-label={`${Points} ${Localized.Labels.points_earned}, text`}
              accessibilityLabel={`${Points} ${Localized.Labels.points_earned}, text`}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            >
              {`${Points} ${Localized.Labels.points_earned}`}
            </AVText>
          )}
        </View>
        <View style={styles.amountDateView}>
          <AVFormattedCurrency
            accessible={true}
            aria-label={`${formattedCurrency}, `}
            accessibilityLabel={`${formattedCurrency}, `}
            style={[
              getDescriber().historyListStyleDescriptor().amount,
              getDescriber().historyListStyleDescriptor().historyTabFontFamily,
              {
                color:
                  [
                    AccountConstants.FUNDING_TYPE,
                    AccountConstants.REFERRAL_TYPE,
                    AccountConstants.REDEMPTION_TYPE,
                    AccountConstants.ADJUSTMENT_TYPE,
                    AccountConstants.SEND_SNACK_ACCEPT,
                    AccountConstants.TRANSFER_IN,
                    AccountConstants.REFERRAL_SIGNUP_TYPE,
                  ].indexOf(Type) > -1 && Amount > 0
                    ? getDescriber().fundingAmountColor()
                    : descriptionTextColor,
              },
            ]}
            value={Amount}
            currency={currency || ''}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            testID="qa_PurchasePrice_txt"
            nativeID="qa_PurchasePrice_txt"
          />
          <AVText
            accessible={true}
            aria-label={`${date}, `}
            accessibilityLabel={`${date}, `}
            style={[
              styles.dateStyle,
              getDescriber().historyListStyleDescriptor().historyTabFontFamily,
            ]}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            testID="qa_receiptDate_txt"
            nativeID="qa_receiptDate_txt"
          >
            {date}
          </AVText>
        </View>

        <View
          style={styles.arrow}
          accessible={true}
          accessibilityLabel={`Right arrow icon, Double tap to ${Localized.Labels.view_specfic_transaction}`}
          aria-label={`Right arrow icon, Double tap to ${Localized.Labels.view_specfic_transaction}`}
        >
          {isItemClickable && (
            <RightIcon size={22} color={getDescriber().historyDateBtnColor()} />
          )}
        </View>
      </View>
    </AVTouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Styles.white,
    flexDirection: 'column',
  },
  mainContainer: {
    flexDirection: 'row',
    padding: Styles.Heights.h1,
    alignItems: 'center',
    backgroundColor: Styles.white,
  },
  leftViewContainer: {
    flex: 1,
  },
  amountDateView: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingRight: Styles.Spacing.m3,
  },
  dateStyle: {
    alignSelf: 'flex-end',
    color: Settings.isRefiveAnd365Pay()
      ? Styles.footerTabInactive
      : Styles.lightGray,
    flex: 0.5,
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '400',
  },
  arrow: {
    width: Styles.Fonts.f1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
